<template>
  <div class="datagrid-component" v-cloak>
    <div class="datagrid-header">
        <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
                <slot name="title"></slot>
            </div>

            <div>
                <slot name="page-actions" :filters="filtersMapped" :selectedItems="selectedItems" :sortingColumn="sortingColumn" :sortingDirection="sortingDirection"></slot>
            </div>
        </div>


        <div class="filter-panel-wrapper">
            <filter-panel
                :search="search"
                :filters="filters"
                :onchange="filterOnChange"
                :disabled="loading"
            ></filter-panel>
        </div>

        <div class="columns-picker">
            <dropdown-multichoice
                ref="datagridColumnsPicker"
                type="icon-button"
                name="columns_picker"
                icon="fal fa-cog"
                :title="trans('columns-picker-title')"
                :options="columnsConfigDropdownMapped"
                :checked="columnsVisibleMapped"
                :hideCountBadge="true"
                :alignRight="true"
                :onsubmit="columnsOnChange"
                :onreset="columnsOnChange"
            ></dropdown-multichoice>
        </div>
    </div>

    <div class="datagrid-body-wrapper" :class="{'loading': loading}">
        <div class="datagrid-body" :style="{'max-height': gridMaxHeight + 'px'}">
            <table class="table">
                <thead>
                    <tr>
                        <th class="checkbox-column" v-if="batchActionsSet"></th>
                        <th v-for="column in columnsVisible"
                            :title="column.label"
                            @click="sortBy(column)"
                            :class="{ 'sortable-column': column.sortType !== '', 'active': column.name === sortingColumn }"
                            :style="{'min-width': column.minWidth ? column.minWidth + 'px' : 'auto', 'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto'}"
                        >
                            <div class="d-flex justify-content-start">
                                <span class="column-label">{{ column.label }}</span>
                                <span class="sorting-indicator">
                                    <i class="fad fa-fw fa-sort" v-if="column.name !== sortingColumn"></i>
                                    <i class="fad fa-fw"
                                         v-if="column.name === sortingColumn"
                                        :class="{ 'fa-sort-down': sortingDirection === 'desc', 'fa-sort-up': sortingDirection === 'asc' }"></i>
                                </span>
                            </div>
                        </th>
                        <th class="actions-column fixed" :class="{'mobile': !isDesktopResolution}">
                            <!-- placeholder for columns picker which have to be outside of datagrid-body and table due to sticky+overflow issues -->
                            <div class="columns-picker-placeholder"></div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in sortedData" :class="getHighlightedClasses(idx)">
                        <td class="checkbox-column" v-if="batchActionsSet">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" :id="'checkbox_'+item.id" :value="item.id" @change="rowCheckboxCheck(item.id)">
                                <label class="form-check-label" :for="'checkbox_'+item.id"></label>
                            </div>
                        </td>
                        <td v-for="column in columnsVisible">
                            <slot :name="'row-td-formatter-'+column.name" :item="item" :options="options">
                                <div :class="'type-'+column.sortType">
                                    <span v-if="column.sortType === 'money' && item[column.name]">{{ item[column.name] }} {{ options.currencySymbol }}</span>
                                    <span v-else-if="column.sortType === 'money'">&ndash;</span>
                                    <span v-else>{{ item[column.name] }}</span>
                                </div>
                            </slot>
                        </td>
                        <td class="actions-column fixed" :class="{'mobile': !isDesktopResolution}">
                            <span class="gradient"></span>
                            <div v-if="isDesktopResolution">
                                <slot name="row-actions" :item="item"></slot>
                            </div>
                            <div class="btn-group" v-else>
                                <button type="button" class="btn btn-light dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <i class="fal fa-fw fa-ellipsis-v"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <slot name="row-actions" :item="item"></slot>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!data || data.length === 0">
                        <td :colspan="columnsVisible.length + 2" class="no-data">
                            <div class="msg">{{ trans('label-no-data') }}</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="datagrid-footer">
        <div class="batch-actions-panel" v-if="selectedItems.length > 0">
            <div class="form-check">
                <input type="checkbox" class="form-check-input" id="checkbox_batch_all" @change="batchCheckboxCheckAll">
                <label class="form-check-label" for="checkbox_batch_all"></label>
            </div>
            <span class="ml-3 mr-4">{{ trans('selected-rows') }}: <b>{{ selectedItems.length }}</b></span>
            <slot name="batch-actions" :selectedItems="selectedItems"></slot>
        </div>

        <div class="total-rows-panel">
             {{ trans('total-rows') }}: <b>{{ data.length }}</b>
        </div>
    </div>

    <slot name="extra-logic" :refresh="refresh"></slot>
  </div>
</template>

<script>
    import FilterPanel from './FilterPanel.vue';
    import TranslationsUtils from "../../app-components/TranslationsUtils";
    import { sortData } from "../../utils";

    Vue.component('filter-panel', FilterPanel);

    export default {
        mixins: [TranslationsUtils],
        props: {
            'resource': { type: Object },
            'defaultSortColumn': { type: String },
            'defaultSortDirection': { type: String, default: 'asc' },
        },

        data() {
            return {
                'uniqueKey': this.resource.uniqueName,
                'listingRoute': this.resource.listingRoute,
                'componentName': 'datagrid', // this is used in TranslationUtils
                'columnsVisible': this.resource.selectedColumns, // user-defined or ALL (if user does not have defined for this unique-key grid)
                'columns': this.resource.columns,
                'filters': this.resource.filters,
                'search': this.resource.search,
                'highlighted': this.resource.highlighted,
                'options': this.resource.options,
                'loading': false,
                'data': this.resource.data,
                'gridMaxHeight': 100,
                'gridFooterOffset': 115,
                'selectedItems': [],
                'sortingColumn': this.defaultSortColumn,
                'sortingDirection': this.defaultSortDirection,
            };
        },

        mounted() {
            // recalculate height on first load + every resize event
            const datagridBody = this.$el.querySelector('.datagrid-body');
            if (datagridBody) {
                this.gridMaxHeight = window.innerHeight - datagridBody.offsetTop - this.gridFooterOffset;
            }

            window.addEventListener('resize', () => {
                this.gridMaxHeight = window.innerHeight - datagridBody.offsetTop - this.gridFooterOffset;
            });

            // load saved sorting state
            if (sessionStorage.getItem(this.sessionSortingKeyName) !== null) {
                let storedState = JSON.parse(sessionStorage.getItem(this.sessionSortingKeyName));

                if(storedState.sortingColumn && storedState.sortingDirection) {
                    this.sortingColumn = storedState.sortingColumn;
                    this.sortingDirection = storedState.sortingDirection;
                }
            }
        },

        methods: {
            rowCheckboxCheck(itemId) {
                if (this.selectedItems.includes(itemId)) {
                    this.selectedItems.splice(this.selectedItems.indexOf(itemId), 1);
                } else {
                    this.selectedItems.push(itemId);
                }

                // race condition, because we need to wait for vue to draw an element
                setTimeout(() => {
                    let chb = this.$el.querySelector('#checkbox_batch_all');
                    if(!chb) return; // in case, vue still not drawed element, this will prevent error

                    if (this.selectedItems.length === 0) {
                        chb.checked = false;
                    } else if (this.selectedItems.length === this.data.length) {
                        chb.checked = true;
                    } else {
                        chb.indeterminate = true;
                    }
                }, 300);
            },

            batchCheckboxCheckAll(evt) {
                let allChecks = this.$el.querySelectorAll('tbody tr td.checkbox-column .form-check-input');
                for(let x in allChecks) {
                    if(typeof allChecks[x] !== 'object') continue;

                    allChecks[x].checked = evt.target.checked;
                }

                if (evt.target.checked) {
                    this.selectedItems = this.data.map(x => x.id);
                } else {
                    this.selectedItems = [];
                }
            },

            batchCancelSelection() {
                this.selectedItems = [];

                let allChecks = this.$el.querySelectorAll('tbody tr td.checkbox-column .form-check-input');
                for(let x in allChecks) {
                    if(typeof allChecks[x] !== 'object') continue;

                    allChecks[x].checked = false;
                }
            },

            refresh() {
                this.loading = true;

                axios.get(this.listingRoute)
                    .then((response) => {
                        if(response && response.data && response.data.uniqueName === this.uniqueKey) {
                            this.data = response.data.data;
                            this.search = response.data.search;
                            this.filters = response.data.filters;
                            this.columnsVisible = response.data.selectedColumns;

                            this.$refs.datagridColumnsPicker.updateCheckedItems(this.columnsVisibleMapped);
                            this.batchCancelSelection();
                        }
                    })
                    .catch(error => {
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response && error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    })
                    .finally(() => this.loading = false);
            },

            filterOnChange(searchValue, filtersValues) {
                this.loading = true;

                const filterTransformed = Object.fromEntries(
                    Object.entries(filtersValues).map(([key, { key: keyStr, values }]) => [keyStr, values])
                );

                axios.post('/datagrid/set-filter', {
                    'datagrid': this.uniqueKey,
                    'search': searchValue,
                    'filter': filterTransformed
                })
                    .then((response) => {
                        this.refresh();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    });
            },

            columnsOnChange(selectedColumns) {
                this.loading = true;

                axios.post('/datagrid/set-columns', {
                    'datagrid': this.uniqueKey,
                    'columns': selectedColumns,
                })
                    .then((response) => {
                        this.refresh();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.$notify({
                          type: 'error',
                          title: 'Error!',
                          text: error.response.data.message ? error.response.data.message : 'An error has occured.'
                        });
                    });
            },

            sortBy(column) {
                if(!column.sortType) {
                    return;
                }

                if(column.name === this.sortingColumn) {
                    this.sortingDirection = this.sortingDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortingDirection = 'asc';
                }

                this.sortingColumn = column.name;

                // keep saved state in session storage
                sessionStorage.setItem(this.sessionSortingKeyName, JSON.stringify({
                    'sortingColumn': this.sortingColumn,
                    'sortingDirection': this.sortingDirection
                }));
            },

            getHighlightedClasses(idx) {
                if(!this.highlighted) return '';

                let classes = [];

                for(let x in this.highlighted) {
                    if(this.highlighted[x].indexOf(idx) >= 0) {
                        classes.push('highlight-' + x);
                    }
                }

                if(classes.length > 0) {
                    classes.push('highlighted');
                };

                return classes.join(' ');
            }
        },

        computed: {

            isDesktopResolution: {
                get() {
                    return window.screen.width >= 990; // more than max tablet resolutions
                }
            },

            sortedData: {
                get() {
                    let column = this.sortingColumn;
                    let columnObj = this.columnsVisible.find(x => x.name === column);

                    if(!columnObj) return this.data;

                    return sortData(this.data, column, columnObj.sortType, this.sortingDirection);
                },
                set() {}
            },

            columnsConfigDropdownMapped: {
                get() {
                    return this.columns.map(x => {
                        return {
                            'label': x.label,
                            'value': x.name,
                        };
                    });
                },
                set() {}
            },

            columnsVisibleMapped: {
                get() {
                    return this.columnsVisible.map(x => x.name);
                },
                set() {}
            },

            sessionSortingKeyName: {
                get() {
                    return 'sortingState-' + this.uniqueKey;
                },
                set() {}
            },

            filtersMapped: {
                get() {
                    // map to key => values format
                    return this.filters.reduce((acc, item) => { acc[item.key] = item.values; return acc; }, {});
                },
                set() {}
            },

            batchActionsSet: {
                get() {
                    return !!this.$scopedSlots['batch-actions'];
                },
                set() {}
            }
        },
    }
</script>


<style lang="scss">
    // override problematic overflows preventing proper position: sticky functioning  - probably not needed (it was used during development)
    /*.app-body, main, #app { overflow-x: initial !important; overflow: initial !important; }*/
    main { padding-bottom: 0 !important; }

    .search-wrapper {
        .form-control {
            min-width: 100px;
        }
    }

    .datagrid-header {
        background-color: white;
        color: #4273FA;
        font-weight: 600;
        padding: 1.25rem;
        border: 1px solid #d6dee3;
        border-bottom: 0px;
        position: relative;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;

        .columns-picker {
           position: absolute;
           right: 30px;
           bottom: -36px;
           z-index: 10;

           .dropdown-menu {
                min-width: 190px;
           }
        }
    }

    .datagrid-body-wrapper {
        overflow-x: hidden;
        background: white;
        padding: 0 1.25rem;
        border: 1px solid #d6dee3;
        border-top: 0px;
        border-bottom-left-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;

        &.loading {
            overflow: hidden !important;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 10;
                background: rgba(255, 255, 255, 0.6);
                //width: 99999px;  // hack: fix position absolute when in scrolled wrapper
                //height: 99999px;
            }

            &:after {
                font-family: "Font Awesome 5 Pro";
                content: "\f1ce";
                color: #485779;
                font-weight: 400;
                font-style: normal;
                font-variant: normal;
                text-rendering: auto;
                font-size: 2rem;
                animation: fa-spin 2s infinite linear;
                position: absolute;
                top: calc(50% - 15px);;
                vertical-align: middle;
                left: calc(50% - 15px);
                z-index: 11;
            }
        }
    }

    .datagrid-body {
        width: 100%;
        max-height: 100px;
        min-height: 100px;
        overflow: auto; /* Povolenie oboch scrollov */
        position: relative;
        margin-bottom: 15px;

        .table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
            margin-bottom: 0 !important;

            thead {
                position: sticky;
                background-color: #e1e8f9;
                z-index: 9;
                top: 0px;
                box-shadow: 0 -8px #f5f8ff;

                th {
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow-x: hidden;
                    /*overflow-x: hidden;*/
                    background: #e1e8f9;
                    font-size: 0.75rem;
                    padding-top: 0px;
                    padding-bottom: 0px;
                    user-select: none;

                    // thead border
                    &:after {
                        content: ' ';
                        bottom: 0px;
                        position: absolute;
                        width: 100%;
                        height: 1px;
                        background: #b9c8de;
                        left: 0;
                        display: block;
                        z-index: 4;
                    }

                    &.fixed:after {
                        display: none !important;
                    }

                    &.sortable-column {
                        user-select: none;
                        cursor: pointer;
                        overflow: hidden;

                        .sorting-indicator {
                            position: relative;
                            padding-left: 0.5rem;

                            .fad {
                                color: #59698f;
                                font-size: 0.6rem;
                            }
                        }

                        .column-label {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }

                        &.active {
                            background-color: #d6def2 !important;

                            .sorting-indicator .fad {
                                color: #111;
                            }
                        }
                    }

                    &.actions-column {
                        text-align: right;
                        min-width: 120px;

                        &.fixed {
                            position: sticky;
                            right: 0;
                            top: 0px;
                            height: 100%;
                            background: #e1e8f9;
                            z-index: 3;
                        }

                        &.mobile {
                            min-width: auto !important;
                            width: 60px !important;
                        }

                        .columns-picker-placeholder {
                          height: 35px;
                          width: 55px;
                        }
                    }
                }
            }

            tbody {
                background: white;

                tr {
                    td {
                        background: #fff;

                        .type-money {
                            text-align: right;
                        }
                        .type-date {
                            text-align: center;
                        }

                        &.actions-column {
                            text-align: right;
                            white-space: nowrap;

                            &.fixed {
                                position: sticky;
                                right: 0;
                                background: white;
                                z-index: 3;
                                padding-left: 30px;

                                span.gradient {
                                    display: block;
                                    position: absolute;
                                    top: 0;
                                    left: -10px;
                                    width: 10px;
                                    height: 100%;
                                    background: linear-gradient(270deg, white 1%, rgba(255, 255, 255, 0) 100%);
                                }
                            }

                            &.mobile {
                                padding-left: 0px !important;
                            }

                            .item-title {
                                display: none;
                            }


                            .dropdown-toggle {
                                padding: 4px 10px;
                                color: black !important;

                                &:after {
                                    display: none !important;
                                }

                                .fa-fw {
                                    font-size: 1.3rem;
                                }
                            }

                            .open {
                                .dropdown-toggle {
                                    background: #d6def2 !important;
                                    color: black !important;
                                }
                                .dropdown-menu {
                                    z-index: 5;
                                    display: flex;
                                    flex-direction: column;
                                    align-items: start;
                                    justify-content: stretch;
                                    left: auto;
                                    right: 62px;
                                    top: 2px;

                                    .btn {
                                        text-align: left;
                                        padding: 0.5rem 1rem;

                                        .fa, .fas, .far, .fal, .fad, .fab {
                                            margin-right: 1rem;
                                        }

                                        .item-title {
                                            display: inline;
                                        }
                                    }
                                }
                            }

                            a {
                                margin-right: 4px;
                            }
                        }

                        &.checkbox-column {
                            width: 43px;
                            .form-check {
                                margin-top: -7px;
                            }
                        }

                        &.no-data {
                          text-align: center;
                          color: #536c79;
                          user-select: none;
                          background: white !important;
                          font-size: 0.8rem;
                          height: 150px;

                          .msg {
                            max-width: 99vw;
                          }
                        }
                    }

                    &:nth-child(2n) td {
                        background: #F9FBFF;

                        &.actions-column.fixed {
                            background: #F9FBFF;

                            span.gradient {
                                background: linear-gradient(270deg, #F9FBFF 1%, rgba(255, 255, 255, 0) 100%);
                            }
                        }
                    }

                    &:hover td, &:hover td.actions-column.fixed {
                        background: #EBF0FD;

                        span.gradient {
                            background: linear-gradient(270deg, #EBF0FD 1%, #EBF0FD00 100%);
                        }
                    }

                    &.highlighted td:first-child {
                        position: relative;
                        padding-left: calc( 0.5rem + 3px);

                        &:before {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: 0;
                            bottom: 0;
                            width: 3px;
                            z-index: 3;
                        }
                    }

                    &.highlight-muted td { color: #536c79; }
                    &.highlight-primary td:first-child:before { background: #4273fa; }
                    &.highlight-success td:first-child:before { background: #60cf7d; }
                    &.highlight-info td:first-child:before { background: #20a8d8; }
                    &.highlight-secondary td:first-child:before { background: #6c757d; }
                    &.highlight-warning td:first-child:before { background: #ffc107; }
                    &.highlight-danger td:first-child:before { background: #fe3f61; }
                    &.highlight-dark td:first-child:before { background: #29363d; }
                    &.highlight-light td:first-child:before { background: #f0f3f5; }
                }
            }
        }
    }

    .datagrid-footer {
        position: relative;
        background: #f5f8ff;
        padding: 0 8px;
        font-size: 0.8rem;
        display: flex;
        align-items: center;
        min-height: 44px;
        border-bottom-left-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;

        .batch-actions-panel {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            background-color: #e1e8f9;
            border-radius: 3px;
            margin: -3px 25px 0 -9px;
            padding-left: 9px;

            .form-check { margin-bottom: 0px; }
            .form-check-label { margin-bottom: 15px; }
        }

        // problem so scrollbarmi v rôznych OS/browseroch... niektoré sú širšie, iné užšie a toto ich prekrýva
        /*&:before {
            content: '';
            display: block;
            position: absolute;
            top: -24px;
            left: 1px;
            right: 1px;
            height: 25px;
            background: linear-gradient(0deg, white 1%, rgba(255, 255, 255, 0) 100%);
            border-bottom: solid #d6dee3 1px;
            border-bottom-left-radius: 0.25rem;
            border-bottom-right-radius: 0.25rem;
            z-index: 3;
        }*/
    }
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/datagrid/Datagrid.vue